import React, { useState, useEffect } from 'react';
import { authFetch } from './api';
// Decision Workspace design (owner ruling 2026-10-21): right-side panel, kit banners and tokens.
import { Banner, Sheet } from '@platform/ui-kit/workspace';
import { WS_CARD, WS_COLOR, WS_STATUS, wsType } from '../workspace/ws';

interface EditScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  applicationId: string;
  applicationName: string;
  integrationGroupId: string;
  currentStartDate: string | null;
  currentEndDate: string | null;
  viewOnly?: boolean;
}

interface ConstraintInfo {
  type: string;
  description: string;
  date: string;
  sourceApp?: string;
  sourcePhaseName?: string;
}

interface DependencyInfo {
  sourceAppId: string;
  sourceAppName: string;
  dependencyType: string;
  sourcePhaseName: string;
  overlapDays: number;
}

interface EditConstraintsData {
  application: {
    id: string;
    name: string;
    currentStartDate: string | null;
    currentEndDate: string | null;
    totalEffortDays: number;
    status: string;
  };
  group: {
    id: string;
    name: string;
    startDate: string | null;
  };
  dependencies: DependencyInfo[];
  constraints: ConstraintInfo[];
  minStartDate: string | null;
}

const EditScheduleModal: React.FC<EditScheduleModalProps> = ({
  isOpen,
  onClose,
  onSave,
  applicationId,
  applicationName,
  integrationGroupId,
  currentStartDate,
  currentEndDate,
  viewOnly = false
}) => {
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [constraintsData, setConstraintsData] = useState<EditConstraintsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);

  // Fetch constraints when modal opens
  useEffect(() => {
    if (isOpen && applicationId && integrationGroupId) {
      fetchConstraints();
    }
  }, [isOpen, applicationId, integrationGroupId]);

  // Set initial date when constraints are loaded
  useEffect(() => {
    if (constraintsData?.application?.currentStartDate) {
      setSelectedDate(constraintsData.application.currentStartDate.split('T')[0]!);
    } else if (constraintsData?.minStartDate) {
      setSelectedDate(constraintsData.minStartDate);
    }
  }, [constraintsData]);

  const fetchConstraints = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(
        `/api/roadmap-schedule/${applicationId}/edit-constraints?integration_group_id=${integrationGroupId}`
      );
      const result = await response.json();

      if (result.success) {
        setConstraintsData(result.data);
      } else {
        setError(result.error || 'Failed to fetch constraints');
      }
    } catch (err: any) {
      setError(err.message || 'Error fetching constraints');
    } finally {
      setLoading(false);
    }
  };

  const validateDate = async (dateStr: string) => {
    if (!dateStr) return;

    try {
      const response = await authFetch('/api/roadmap-schedule/validate-start-date', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          application_id: applicationId,
          integration_group_id: integrationGroupId,
          proposed_start_date: dateStr
        })
      });

      const result = await response.json();

      if (result.success) {
        setValidationErrors(result.errors || []);
        setValidationWarnings(result.warnings || []);
      }
    } catch (err) {
      console.error('Error validating date:', err);
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    setValidationErrors([]);
    setValidationWarnings([]);

    // Validate after a short delay
    if (newDate) {
      validateDate(newDate);
    }
  };

  const handleSave = async () => {
    if (!selectedDate || validationErrors.length > 0) return;

    setSaving(true);
    setError(null);

    try {
      const response = await authFetch(`/api/roadmap-schedule/${applicationId}/update-schedule`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          new_start_date: selectedDate,
          integration_group_id: integrationGroupId,
          recalculate_dependents: true
        })
      });

      const result = await response.json();

      if (result.success) {
        onSave();
        onClose();
      } else {
        setError(result.error || 'Failed to update schedule');
      }
    } catch (err: any) {
      setError(err.message || 'Error updating schedule');
    } finally {
      setSaving(false);
    }
  };

  const formatDisplayDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  // DW: the window is now a right-side panel (Decision Workspace Sheet, single width);
  // its fields, constraints, validation and buttons are unchanged.
  const sectionStyle: React.CSSProperties = {
    marginBottom: '20px'
  };

  const sectionTitleStyle: React.CSSProperties = {
    ...wsType('sectionTitle'),
    margin: '0 0 12px 0',
    color: WS_COLOR.text
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    ...wsType('label'),
    color: WS_COLOR.text,
    marginBottom: '6px'
  };

  const infoBoxStyle: React.CSSProperties = {
    ...WS_CARD,
    background: WS_COLOR.surfaceSubtle,
    padding: '12px 16px',
    ...wsType('table')
  };

  const messageStackStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  };

  const mutedStyle: React.CSSProperties = { color: WS_COLOR.textMuted };

  const saveDisabled = saving || !selectedDate || validationErrors.length > 0 || loading;

  return (
    <Sheet
      open={isOpen}
      title={viewOnly ? 'View Schedule Details' : 'Edit Schedule'}
      onClose={onClose}
      width={531}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {viewOnly ? 'Close' : 'Cancel'}
          </button>
          {!viewOnly && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saveDisabled}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </>
      }
    >
      <p style={{ margin: '0 0 16px 0', ...wsType('table'), color: WS_COLOR.textMuted }}>
        {applicationName} ({applicationId})
      </p>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', ...wsType('table'), color: WS_COLOR.textMuted }}>
          <p>Loading constraints...</p>
        </div>
      ) : error ? (
        <Banner tone="error">
          <strong>Error:</strong> {error}
        </Banner>
      ) : (
        <>
          {/* Current Schedule Info */}
          <div style={sectionStyle}>
            <h3 style={sectionTitleStyle}>
              Current Schedule
            </h3>
            <div style={infoBoxStyle}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <span style={mutedStyle}>Start Date: </span>
                  <strong>{formatDisplayDate(constraintsData?.application?.currentStartDate || currentStartDate)}</strong>
                </div>
                <div>
                  <span style={mutedStyle}>End Date: </span>
                  <strong>{formatDisplayDate(constraintsData?.application?.currentEndDate || currentEndDate)}</strong>
                </div>
                <div>
                  <span style={mutedStyle}>Total Effort: </span>
                  <strong>{constraintsData?.application?.totalEffortDays || '-'} days</strong>
                </div>
                <div>
                  <span style={mutedStyle}>Status: </span>
                  <strong>{constraintsData?.application?.status || '-'}</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Constraints Section */}
          {constraintsData && (constraintsData.constraints.length > 0 || constraintsData.dependencies.length > 0) && (
            <div style={sectionStyle}>
              <h3 style={sectionTitleStyle}>
                Scheduling Constraints
              </h3>

              <div style={messageStackStyle}>
                {/* Group Constraint */}
                {constraintsData.group?.startDate && (
                  <Banner tone="warning">
                    <strong>Integration Group Constraint:</strong><br />
                    Start date cannot be before {formatDisplayDate(constraintsData.group.startDate)}
                    (Group: {constraintsData.group.name})
                  </Banner>
                )}

                {/* Dependency Constraints */}
                {constraintsData.dependencies.map((dep, idx) => (
                  <Banner key={idx} tone="warning">
                    <strong>
                      {dep.dependencyType === 'phase_based' ? 'Overlapping' : 'Sequential'} Dependency:
                    </strong><br />
                    {dep.dependencyType === 'phase_based' ? (
                      <>
                        Source: <strong>{dep.sourceAppName || dep.sourceAppId}</strong><br />
                        Must wait for "{dep.sourcePhaseName}" phase to start
                        {dep.overlapDays > 0 && ` (${dep.overlapDays} days overlap allowed)`}
                      </>
                    ) : (
                      <>
                        Source: <strong>{dep.sourceAppName || dep.sourceAppId}</strong><br />
                        Must start after source application completes
                      </>
                    )}
                  </Banner>
                ))}

                {/* Minimum Start Date */}
                {constraintsData.minStartDate && (
                  <div style={{ marginTop: '4px' }}>
                    <Banner tone="info">
                      <strong>Earliest Allowed Start Date:</strong> {formatDisplayDate(constraintsData.minStartDate)}
                    </Banner>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Date Picker Section - Hidden in viewOnly mode */}
          {!viewOnly && (
            <>
              <div style={sectionStyle}>
                <h3 style={sectionTitleStyle}>
                  Select New Start Date
                </h3>
                <label style={labelStyle}>Start Date</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  min={constraintsData?.minStartDate || undefined}
                  aria-invalid={validationErrors.length > 0}
                  style={{
                    width: '100%',
                    ...(validationErrors.length > 0 ? { borderColor: WS_STATUS.error.ink } : null)
                  }}
                />
              </div>

              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div style={{ ...sectionStyle, ...messageStackStyle }}>
                  {validationErrors.map((err, idx) => (
                    <Banner key={idx} tone="error">
                      {err}
                    </Banner>
                  ))}
                </div>
              )}

              {/* Validation Warnings */}
              {validationWarnings.length > 0 && (
                <div style={{ ...sectionStyle, ...messageStackStyle }}>
                  {validationWarnings.map((warn, idx) => (
                    <Banner key={idx} tone="warning">
                      {warn}
                    </Banner>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </Sheet>
  );
};

export default EditScheduleModal;
